import { RESTRICTABLE_KINDS, type RestrictableKind } from "@repo/embed";
import { z } from "zod";

/**
 * What WhatsApp's commerce policy never lets a business list in its Meta catalog beyond
 * alcohol and tobacco (C17): medicines and health products, drugs, adult products, hazardous
 * goods, weapons. Judged like the others, never offered as a retailer restriction: only the
 * Meta catalog excludes them (`metaExcludedKinds` in @repo/catalog).
 */
export const META_POLICY_KINDS = [
	"medical",
	"drugs",
	"adult",
	"hazardous",
	"weapons",
] as const;
export type MetaPolicyKind = (typeof META_POLICY_KINDS)[number];

/**
 * What a product can be that some retailers never sell, as judged per product
 * (ProductContentFlags.kinds). `doubtful`: may contain pork or alcohol although its name does
 * not say (gelatin of unknown origin, a liqueur filling). The Meta-policy kinds follow.
 */
export const CONTENT_KINDS = [
	...RESTRICTABLE_KINDS,
	"doubtful",
	...META_POLICY_KINDS,
] as const;
export type ContentKind = (typeof CONTENT_KINDS)[number];

/** The kinds a retailer can exclude (one list with the app's). `doubtful` follows alcohol and pork. */
export { RESTRICTABLE_KINDS, type RestrictableKind };

/** Islamic dietary rules: no alcohol, no alcohol-free beer, wine or spirits, no pork. */
export const HALAL_KINDS: readonly RestrictableKind[] = [
	"alcohol",
	"alcohol_free",
	"pork",
];

/**
 * Products the retailer never shows, suggests or sells through the agent. Enforced on the
 * server at every path a product reaches a shopper or a basket, not by the prompt alone.
 */
export const restrictionsSchema = z
	.object({
		exclude: z
			.array(z.enum(RESTRICTABLE_KINDS))
			.default([])
			.transform((kinds) =>
				RESTRICTABLE_KINDS.filter((kind) => kinds.includes(kind)),
			),
		/**
		 * Also hide products that may contain pork or alcohol although their name does not say
		 * (gummies with gelatin of unstated origin, vanilla extract). Off for a market where
		 * everything sold is certified, such as the UAE, at the retailer's word.
		 */
		hideDoubtful: z.boolean().default(true),
	})
	.prefault({});
export type Restrictions = z.infer<typeof restrictionsSchema>;

/** Every judged kind that hides a product: `doubtful` too once alcohol or pork is excluded. */
export function excludedKinds(
	exclude: readonly RestrictableKind[],
	hideDoubtful = true,
): ContentKind[] {
	const kinds: ContentKind[] = [...exclude];
	if (
		hideDoubtful &&
		(exclude.includes("alcohol") || exclude.includes("pork"))
	) {
		kinds.push("doubtful");
	}
	return kinds;
}

/** The union of two exclusion lists: an app may add restrictions, never remove them. */
export function tightenRestrictions(
	exclude: readonly RestrictableKind[],
	extra: readonly RestrictableKind[] | undefined,
): RestrictableKind[] {
	return RESTRICTABLE_KINDS.filter(
		(kind) => exclude.includes(kind) || extra?.includes(kind),
	);
}

/**
 * What `next` allows that `live` did not: a kind no longer excluded, or doubtful products shown
 * again. Loosening a live restriction is Hephon's to do; adding one is anyone's.
 */
export function loosened(live: Restrictions, next: Restrictions): string[] {
	return [
		...live.exclude.filter((kind) => !next.exclude.includes(kind)),
		...(live.hideDoubtful && !next.hideDoubtful && live.exclude.length
			? ["doubtful"]
			: []),
	];
}
